// auto completer para filtro de servicio de courier
import React, { useState, useEffect, useRef } from 'react'
import {
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material'
import { Search, Clear } from '@mui/icons-material'
import { ComponentProps } from '../../../../types/filters'
import { CourierService } from '../../../../interfaces/Courier'
import { getCourierListCached } from './courierListCache'

interface CourierServiceOption extends CourierService {
  courierName: string
}

interface CourierServiceAutoCompleterProps extends ComponentProps {
  // Si viene en true, trae también los servicios de couriers inactivos (onlyActive=false)
  includeInactive?: boolean
}

export const CourierServiceAutoCompleter: React.FC<CourierServiceAutoCompleterProps> = ({
  value,
  onChange,
  fullWidth = true,
  includeInactive
}) => {
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState<CourierServiceOption | null>(null)
  const [allServices, setAllServices] = useState<CourierServiceOption[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoading(true)
    getCourierListCached(includeInactive ? false : undefined)
      .then((couriers) => {
        const services = couriers.flatMap((courier) =>
          courier.courierServices.map((service) => ({
            ...service,
            courierName: courier.name
          }))
        )
        setAllServices(services)
      })
      .catch(() => setAllServices([]))
      .finally(() => setLoading(false))
  }, [includeInactive])

  useEffect(() => {
    if (!value && selected) {
      setSelected(null)
      setSearchText('')
    }
  }, [value])

  const trimmedSearch = searchText.trim()

  const filteredOptions = trimmedSearch.length >= 3
    ? allServices.filter((service) =>
        service.name.toLowerCase().includes(trimmedSearch.toLowerCase())
      )
    : []

  const handleSelect = (service: CourierServiceOption) => {
    setSelected(service)
    setSearchText('')
    setIsOpen(false)

    onChange({
      target: { value: service.name, name: 'courierServiceName' }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleClear = () => {
    setSelected(null)
    setSearchText('')
    onChange({ target: { value: '', name: 'courierServiceName' } } as React.ChangeEvent<HTMLInputElement>)
  }

  const displayValue = selected ? selected.name : searchText

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div ref={anchorRef} style={{ position: 'relative', width: fullWidth ? '100%' : 300 }}>
        <TextField
          value={displayValue}
          onChange={(e) => { setSearchText(e.target.value); setIsOpen(true) }}
          onFocus={() => setIsOpen(true)}
          variant='outlined'
          size='small'
          placeholder='Buscar Servicio de Courier...'
          autoComplete='off'
          fullWidth
          sx={{
            height: '44px',
            '& .MuiInputBase-root': { height: '44px' }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : selected ? (
                    <Clear style={{ cursor: 'pointer' }} onClick={handleClear} />
                  ) : (
                    <Search />
                  )}
                </InputAdornment>
              )
            }
          }}
        />

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          style={{ width: anchorRef.current?.offsetWidth || 'auto', zIndex: 1300 }}
        >
          <Paper elevation={3} style={{ maxHeight: 300, overflow: 'auto' }}>
            {trimmedSearch.length > 0 && trimmedSearch.length < 3 && (
              <MenuItem disabled>Escriba al menos 3 caracteres</MenuItem>
            )}
            {trimmedSearch.length >= 3 && !loading && filteredOptions.length === 0 && (
              <MenuItem disabled>No se encontraron resultados</MenuItem>
            )}
            {filteredOptions.map((service) => (
              <MenuItem
                key={service.courierServiceId}
                onClick={() => handleSelect(service)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 'bold' }}>{service.name}</div>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>
                    Courier: {service.courierName}
                  </div>
                </div>
              </MenuItem>
            ))}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}
